import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { parseCssColor } from "./markerPin";
import { buildRailings } from "./railing";
import { animateWater } from "./waterWaves";
import type { RailingEdge } from "./railing";
import type { RailingStyle } from "../types";

/** Un point de la scène du décor, en mètres : x vers l'est, z vers le sud. */
export type GroundPoint = [number, number];

const DEFAULT_FLOOR_COLOR = "#b7b4ad";

const wallMaterial = new THREE.MeshStandardMaterial({ name: "dug-wall", color: "#bcbab5", roughness: 0.95, metalness: 0, side: THREE.DoubleSide });
const maskMaterial = new THREE.MeshBasicMaterial({ name: "mask", color: "#000000" });
export interface WallEdge {
  a: GroundPoint;
  b: GroundPoint;
  top: number;
}

function openRing(ring: GroundPoint[]): GroundPoint[] {
  const first = ring[0];
  const last = ring[ring.length - 1];
  return first && last && ring.length > 1 && first[0] === last[0] && first[1] === last[1] ? ring.slice(0, -1) : ring;
}

function outline(ring: GroundPoint[]): THREE.Vector2[] {
  return ring.map(([x, z]) => new THREE.Vector2(x, -z));
}

export function surface(outer: GroundPoint[], holes: GroundPoint[][], y: number): THREE.BufferGeometry {
  const shape = new THREE.Shape(outline(outer));
  for (const hole of holes) shape.holes.push(new THREE.Path(outline(hole)));
  return new THREE.ShapeGeometry(shape).rotateX(-Math.PI / 2).translate(0, y, 0);
}

function wallEdges(rings: GroundPoint[][], bottom: number, wallTop: (a: GroundPoint, b: GroundPoint) => number | null): WallEdge[] {
  return rings.flatMap((ring) =>
    ring.flatMap((a, index) => {
      const b = ring[(index + 1) % ring.length]!;
      const top = wallTop(a, b);
      return top === null || top <= bottom ? [] : [{ a, b, top }];
    }),
  );
}

function walls(edges: WallEdge[], bottom: number): THREE.BufferGeometry {
  const positions = edges.flatMap(({ a: [ax, az], b: [bx, bz], top }) => [
    ax, top, az, bx, top, bz, bx, bottom, bz, ax, top, az, bx, bottom, bz, ax, bottom, az,
  ]);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  return geometry;
}

/** Paramètres (0 à 1) où le segment entre dans le polygone convexe et en sort, ou `null` s'il ne le traverse pas. */
function convexCrossing(a: GroundPoint, b: GroundPoint, polygon: GroundPoint[]): [number, number] | null {
  let enter = 0;
  let leave = 1;
  const direction = [b[0] - a[0], b[1] - a[1]];
  const area = polygon.reduce((sum, [x, z], i) => {
    const [nx, nz] = polygon[(i + 1) % polygon.length]!;
    return sum + (x * nz - nx * z);
  }, 0);
  for (let i = 0; i < polygon.length; i++) {
    const [px, pz] = polygon[i]!;
    const [qx, qz] = polygon[(i + 1) % polygon.length]!;
    // Normale intérieure du côté, quel que soit le sens de parcours du polygone.
    const inner = area > 0 ? [-(qz - pz), qx - px] : [qz - pz, -(qx - px)];
    const offset = (a[0] - px) * inner[0]! + (a[1] - pz) * inner[1]!;
    const speed = direction[0]! * inner[0]! + direction[1]! * inner[1]!;
    if (speed === 0) {
      if (offset < 0) return null;
      continue;
    }
    const t = -offset / speed;
    if (speed > 0) enter = Math.max(enter, t);
    else leave = Math.min(leave, t);
    if (enter >= leave) return null;
  }
  return [enter, leave];
}

/** Les morceaux du bord hors des passages : un garde-corps s'y interrompt. */
export function edgeOutsideGaps<E extends { a: GroundPoint; b: GroundPoint }>(edge: E, gaps: GroundPoint[][]): E[] {
  const cuts = gaps
    .map((gap) => convexCrossing(edge.a, edge.b, gap))
    .filter((cut): cut is [number, number] => cut !== null)
    .sort((x, y) => x[0] - y[0]);
  const at = (t: number): GroundPoint => [edge.a[0] + (edge.b[0] - edge.a[0]) * t, edge.a[1] + (edge.b[1] - edge.a[1]) * t];
  if (!cuts.length) return [edge];
  const pieces: E[] = [];
  let from = 0;
  for (const [enter, leave] of cuts) {
    if (enter > from) pieces.push({ ...edge, a: at(from), b: at(enter) });
    from = Math.max(from, leave);
  }
  if (from < 1) pieces.push({ ...edge, a: at(from), b: edge.b });
  return pieces;
}

/** Une bande étroite autour de chaque segment du tracé : le bord qu'elle recouvre est coupé exactement entre ses bouts. */
export function pathGaps(path: GroundPoint[]): GroundPoint[][] {
  const margin = 0.05;
  return path.slice(1).flatMap((to, index) => {
    const from = path[index]!;
    const length = Math.hypot(to[0] - from[0], to[1] - from[1]);
    if (!length) return [];
    const [nx, nz] = [(-(to[1] - from[1]) / length) * margin, ((to[0] - from[0]) / length) * margin];
    return [[[from[0] + nx, from[1] + nz], [to[0] + nx, to[1] + nz], [to[0] - nx, to[1] - nz], [from[0] - nx, from[1] - nz]]];
  });
}

function floorMaterial(color: string | undefined, texture?: THREE.Texture): THREE.MeshStandardMaterial {
  if (texture) return new THREE.MeshStandardMaterial({ name: "dug-floor", map: texture, roughness: 0.95, metalness: 0, side: THREE.DoubleSide });
  const floor = new THREE.Color(DEFAULT_FLOOR_COLOR);
  if (color && color !== "transparent") floor.setStyle(color);
  return new THREE.MeshStandardMaterial({ name: "dug-floor", color: floor, roughness: 0.95, metalness: 0, side: THREE.DoubleSide });
}

/** Une forme plate posée au fond, dans le repère de la scène ; épaisseur de bordure en mètres. */
export interface PitDecal {
  ring: GroundPoint[];
  holes: GroundPoint[][];
  /** Une ligne n'a ni fond ni dernier côté. */
  closed: boolean;
  fillColor?: string;
  borderColor?: string;
  borderWidth: number;
  /** Longueurs d'un tiret et d'un blanc, en mètres. */
  borderDash?: [number, number];
  volume?: PitVolume;
}

/** Un volume posé au fond, de `base` à `top` mètres au-dessus de lui. */
export interface PitVolume {
  base: number;
  top: number;
  color: string;
  opacity: number;
  /** Part de blanc mêlée à la couleur, de 0 à 1. */
  lighten: number;
}

const DECAL_FILL_LIFT = 0.02;
const DECAL_BORDER_LIFT = 0.04;
const MITER_LIMIT = 2;

function decalMaterial(color: string): THREE.MeshStandardMaterial | null {
  const parsed = parseCssColor(color);
  if (!parsed || parsed.alpha <= 0) return null;
  const transparent = parsed.alpha < 1;
  return new THREE.MeshStandardMaterial({
    name: "dug-decal",
    color: new THREE.Color().setStyle(parsed.opaque),
    roughness: 0.95,
    metalness: 0,
    side: THREE.DoubleSide,
    transparent,
    opacity: parsed.alpha,
    depthWrite: !transparent,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -4,
  });
}

/** Un trait d'épaisseur `width` centré sur la ligne, aux angles en onglet. */
function ribbon(points: GroundPoint[], closed: boolean, width: number, y: number): THREE.BufferGeometry {
  const count = points.length;
  const half = width / 2;
  const direction = (from: GroundPoint, to: GroundPoint): GroundPoint => {
    const length = Math.hypot(to[0] - from[0], to[1] - from[1]) || 1;
    return [(to[0] - from[0]) / length, (to[1] - from[1]) / length];
  };
  const sides = points.map((point, index): [GroundPoint, GroundPoint] => {
    const previous = index > 0 ? points[index - 1] : closed ? points[count - 1] : undefined;
    const next = index < count - 1 ? points[index + 1] : closed ? points[0] : undefined;
    const incoming = previous ? direction(previous, point) : direction(point, next!);
    const outgoing = next ? direction(point, next) : incoming;
    const normal: GroundPoint = [-(incoming[1] + outgoing[1]), incoming[0] + outgoing[0]];
    const length = Math.hypot(normal[0], normal[1]) || 1;
    const cos = (normal[0] * -incoming[1] + normal[1] * incoming[0]) / length;
    const offset = half / Math.max(Math.abs(cos), 1 / MITER_LIMIT);
    const [nx, nz] = [(normal[0] / length) * offset, (normal[1] / length) * offset];
    return [[point[0] + nx, point[1] + nz], [point[0] - nx, point[1] - nz]];
  });
  const positions: number[] = [];
  const segments = closed ? count : count - 1;
  for (let i = 0; i < segments; i++) {
    const [a1, a2] = sides[i]!;
    const [b1, b2] = sides[(i + 1) % count]!;
    positions.push(a1[0], y, a1[1], b1[0], y, b1[1], b2[0], y, b2[1], a1[0], y, a1[1], b2[0], y, b2[1], a2[0], y, a2[1]);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  return geometry;
}

function dashed(points: GroundPoint[], closed: boolean, [dash, gap]: [number, number]): GroundPoint[][] {
  const path = closed ? [...points, points[0]!] : points;
  const pieces: GroundPoint[][] = [];
  let current: GroundPoint[] = [path[0]!];
  let drawing = true;
  let left = dash;
  for (let i = 1; i < path.length; i++) {
    let from = path[i - 1]!;
    const to = path[i]!;
    let length = Math.hypot(to[0] - from[0], to[1] - from[1]);
    while (length > left) {
      const t = left / length;
      const cut: GroundPoint = [from[0] + (to[0] - from[0]) * t, from[1] + (to[1] - from[1]) * t];
      if (drawing) pieces.push([...current, cut]);
      else current = [cut];
      drawing = !drawing;
      length -= left;
      from = cut;
      left = drawing ? dash : gap;
    }
    left -= length;
    if (drawing) current.push(to);
  }
  if (drawing && current.length > 1) pieces.push(current);
  return pieces;
}

function volumeMeshes(ring: GroundPoint[], holes: GroundPoint[][], { base, top, color, opacity, lighten }: PitVolume, floor: number): THREE.Mesh[] {
  if (top <= base || opacity <= 0) return [];
  const parsed = parseCssColor(color);
  if (!parsed) return [];
  const transparent = opacity < 1;
  const material = new THREE.MeshStandardMaterial({
    name: "dug-volume",
    color: new THREE.Color().setStyle(parsed.opaque).lerp(new THREE.Color("#ffffff"), lighten),
    roughness: 0.9,
    metalness: 0,
    side: THREE.DoubleSide,
    transparent,
    opacity,
    depthWrite: !transparent,
  });
  const rings = [ring, ...holes];
  const edges = wallEdges(rings, floor + base, () => floor + top);
  const geometry = mergeGeometries([walls(edges, floor + base), surface(ring, holes, floor + top).toNonIndexed()].map((part) => {
    part.deleteAttribute("uv");
    part.deleteAttribute("normal");
    return part;
  }));
  geometry.computeVertexNormals();
  const mesh = new THREE.Mesh(geometry, material);
  if (!transparent) return [mesh];
  const nearestOnly = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({ name: "dug-volume-depth", colorWrite: false, side: THREE.DoubleSide }));
  return [nearestOnly, mesh];
}

function decals(items: PitDecal[], floor: number): THREE.Object3D[] {
  return items.flatMap((decal, index) => {
    const ring = decal.closed ? openRing(decal.ring) : decal.ring;
    if (decal.volume) {
      const meshes = decal.closed && ring.length >= 3 ? volumeMeshes(ring, decal.holes.map(openRing), decal.volume, floor) : [];
      for (const mesh of meshes) mesh.name = "PitDecal";
      return meshes;
    }
    const lift = index * 0.001;
    const meshes: THREE.Mesh[] = [];
    const fill = decal.closed && decal.fillColor ? decalMaterial(decal.fillColor) : null;
    if (fill && ring.length >= 3) meshes.push(new THREE.Mesh(surface(ring, decal.holes.map(openRing), floor + DECAL_FILL_LIFT + lift), fill));
    const border = decal.borderColor && decal.borderWidth > 0 ? decalMaterial(decal.borderColor) : null;
    if (border && ring.length >= 2) {
      const y = floor + DECAL_BORDER_LIFT + lift;
      for (const line of [ring, ...(decal.closed ? decal.holes.map(openRing) : [])]) {
        const dash = decal.borderDash;
        if (dash && dash[0] > 0 && dash[1] > 0) {
          for (const piece of dashed(line, decal.closed, dash)) meshes.push(new THREE.Mesh(ribbon(piece, false, decal.borderWidth, y), border));
        } else {
          meshes.push(new THREE.Mesh(ribbon(line, decal.closed, decal.borderWidth, y), border));
        }
      }
    }
    for (const mesh of meshes) mesh.name = "PitDecal";
    return meshes;
  });
}

/** Les formes posées au fond d'une zone creusée de cette profondeur, seules : un changement de zoom les refait sans toucher au reste. */
export function buildPitDecals(items: PitDecal[], depth: number): THREE.Object3D[] {
  return decals(items, -depth);
}

export interface DugAreaOptions {
  /** Profondeur du fond, en mètres sous le sol. */
  depth: number;
  floorColor?: string;
  /** Matière du fond, à la place de `floorColor`. */
  floorTexture?: THREE.Texture;
  /** Hauteur du bord : le fond de la zone qui contient celle-ci, ou le sol. */
  rim?: number;
  /** Les zones creusées dans le fond de celle-ci, où il est percé. */
  floorHoles?: GroundPoint[][];
  /**
   * Haut de la paroi d'un bord, en mètres (négatif sous le sol) ; `null` pour aucune paroi. Par
   * défaut, toutes les parois partent du sol.
   */
  wallTop?: (a: GroundPoint, b: GroundPoint) => number | null;
  /** Barrière en haut de chaque paroi. */
  railing?: RailingStyle;
  /** Emprises au sol (polygones convexes) des volées qui franchissent le bord : le garde-corps s'y interrompt. */
  railingGaps?: GroundPoint[][];
  /** Formes plates posées au fond. */
  decals?: PitDecal[];
  /** Barrières des formes posées au fond, à leur hauteur par rapport au sol. */
  railings?: { style: RailingStyle; edges: RailingEdge[] }[];
  /** Surface d'eau translucide à `dugWaterLevel` sous le sol. */
  water?: boolean;
}

/** Niveau de l'eau d'une zone creusée, en mètres sous le sol. */
export function dugWaterLevel(depth: number): number {
  return Math.min(0.4, depth / 2);
}

/** Hauteur de l'eau d'une zone creusée dont le bord est à `rim` (le fond d'une autre zone, ou le sol). */
export function pitWaterSurface(depth: number, rim = 0): number {
  return rim - dugWaterLevel(depth + rim);
}

const waterMaterial = animateWater(new THREE.MeshStandardMaterial({
  name: "dug-water",
  color: "#3f8fc7",
  roughness: 0.15,
  metalness: 0.1,
  transparent: true,
  opacity: 0.55,
  depthWrite: false,
  side: THREE.DoubleSide,
}));

export interface DugArea {
  root: THREE.Group;
  /** Les tronçons de barrière au niveau du sol, que le calque du décor dessine avec ceux des autres formes. */
  rim: RailingEdge[];
}

/**
 * Zone creusée dans le sol, à la convention des trémies (`Mask` pour l'ouverture, `Pit…` pour ce qui
 * est dessous) : le calque du décor la dessine à travers le sol comme celles des escalators.
 */
export function buildDugArea(outer: GroundPoint[], holes: GroundPoint[][], options: DugAreaOptions): DugArea {
  const { depth, floorColor, floorTexture, rim = 0, wallTop = () => rim } = options;
  const ring = openRing(outer);
  const openHoles = holes.map(openRing).filter((hole) => hole.length >= 3);

  const pit = new THREE.Group();
  pit.name = "Pit";
  const edges = wallEdges([ring, ...openHoles], -depth, wallTop);
  const sides = new THREE.Mesh(walls(edges, -depth), wallMaterial);
  sides.name = "PitWalls";
  const floorHoles = (options.floorHoles ?? []).map(openRing).filter((hole) => hole.length >= 3);
  const floor = new THREE.Mesh(surface(ring, [...openHoles, ...floorHoles], -depth), floorMaterial(floorColor, floorTexture));
  floor.name = "PitFloor";
  pit.add(sides, floor, ...decals(options.decals ?? [], -depth));
  if (options.water) {
    const water = new THREE.Mesh(surface(ring, openHoles, pitWaterSurface(depth, rim)), waterMaterial);
    water.name = "PitWater";
    water.renderOrder = 1;
    pit.add(water);
  }

  const mask = new THREE.Mesh(surface(ring, openHoles, 0.01), maskMaterial);
  mask.name = "Mask";

  const root = new THREE.Group();
  root.add(pit, mask);
  // Au niveau du sol, la barrière se voit de partout ; plus bas, seulement par l'ouverture.
  const railed = options.railing
    ? edges.flatMap((edge) => edgeOutsideGaps(edge, options.railingGaps ?? [])).map(({ a, b, top }) => ({ a, b, base: top }))
    : [];
  const sunk = [
    ...(options.railing ? [{ style: options.railing, edges: railed.filter((edge) => edge.base < 0) }] : []),
    ...(options.railings ?? []),
  ];
  if (sunk.some(({ edges: list }) => list.length)) pit.add(buildRailings(sunk, "PitRailing"));
  return { root, rim: railed.filter((edge) => edge.base >= 0) };
}
